import { Interpolation, SpringValues, to, TransitionState, useTransition } from '@react-spring/web';

import { usePreviousValue } from '@tanium/react-use-previous-value';

import { getNormalizedAnimatedScaleProps } from '../utils/getNormalizedAnimatedScaleProps';
import { getNormalizedOffsetInterpolator } from '../utils/getNormalizedOffsetInterpolator';
import { getNormalizedValueInterpolator } from '../utils/getNormalizedValueInterpolator';
import { isBandScale } from '../utils/isBandScale';
import { isNumberCoercible } from '../utils/isNumberCoercible';
import { NumberCoercible } from '../utils/NumberCoercible';
import { StringCoercible } from '../utils/StringCoercible';
import { ScaleWithValueType } from '../utils/SupportedScale';

/**
 * Returns a suitably unique React key for a given value. This avoids using `.toString` in cases
 * where the returned string might yield an insufficiently unique result. (For example: `new
 * Date(0).toString() === new Date(1).toString()`)
 *
 * @param value The value to use to generate a key.
 */
const getKeyFromValue = (value: StringCoercible | NumberCoercible): string => {
  if (isNumberCoercible(value)) {
    return value.valueOf().toString();
  }

  return value.toString();
};

const identity = <T>(v: T) => v;

interface TransitionProps {
  opacity: number;
  normalizedValue: number;
  normalizedOffset: number;
}

/**
 * Same as TransitionRenderFn from `@react-spring/web`, except it includes `interpolatedValue` among the
 * values passed to the render function.
 */
interface TransitionRenderFn<TItem, TTransitionProps> {
  (
    values: SpringValues<TTransitionProps> & { interpolatedValue: Interpolation<number, number> },
    item: TItem,
    transition: TransitionState<TItem, TTransitionProps>,
    index: number,
  ): React.ReactNode;
}

/**
 * Same as TransitionFn from `@react-spring/web`, except it uses the overloaded version of
 * `TransitionRenderFn`.
 */
interface TransitionFn<TItem, TTransitionProps> {
  (render: TransitionRenderFn<TItem, TTransitionProps>): JSX.Element;
}

interface UseTickTransition {
  <TTick extends NumberCoercible | StringCoercible>(params: {
    ticks: TTick[];
    scale: ScaleWithValueType<TTick>;
    isAnimated: boolean;
  }): TransitionFn<TTick, TransitionProps>;

  <TTick extends NumberCoercible | StringCoercible, TItem>(params: {
    ticks: TItem[];
    tickGetter: (item: TItem) => TTick;
    scale: ScaleWithValueType<TTick>;
    isAnimated: boolean;
  }): TransitionFn<TItem, TransitionProps>;
}

export const useTickTransition: UseTickTransition = <TTick extends NumberCoercible | StringCoercible, TItem>({
  ticks,
  tickGetter = identity as (item: TItem) => TTick,
  scale,
  isAnimated,
}: {
  ticks: TItem[];
  tickGetter?: (item: TItem) => TTick;
  scale: ScaleWithValueType<TTick>;
  isAnimated: boolean;
}): TransitionFn<TItem, TransitionProps> => {
  const previousScale = usePreviousValue(scale);

  const range = scale.range();

  const [transitionFn] = useTransition<TItem, TransitionProps>(
    ticks,
    {
      keys: (item) => getKeyFromValue(tickGetter(item)),
      initial: {
        // Force the initial opacity to 1 so that items don't fade in when the component is initially
        // mounted, but do fade in if they are added subsequently.
        opacity: 1,
      },
      from: (item) => {
        const commonProps = { opacity: 0 };

        // In a band scale, there is no way to determine where a domain entry would be coming
        // "from," so rather than make one up we allow new band scale items to appear in the correct
        // position.
        if (isBandScale(scale)) {
          return commonProps;
        }

        // For other scales, we can use the previous scale to determine where new items should
        // appear to animate from, which maintains consistency with other animations.
        return { ...commonProps, ...getNormalizedAnimatedScaleProps(tickGetter(item), previousScale) };
      },
      enter: (item) => ({ opacity: 1, ...getNormalizedAnimatedScaleProps(tickGetter(item), scale) }),
      update: (item) => getNormalizedAnimatedScaleProps(tickGetter(item), scale),
      leave: (item) => {
        const commonProps = { opacity: 0 };

        // As with `from`, there is no way to place an item on a band scale that does not include
        // that item in the domain, so we just have the item fade out in place.
        if (isBandScale(scale)) {
          return commonProps;
        }

        // For other scales, we use the new (i.e., current) scale to determine the outgoing item's
        // position. Note that if the scale changes again before the item is fully removed, it may
        // appear to "leave" to a stale location, because its final position is not updated once it
        // starts to "leave".
        return { ...commonProps, ...getNormalizedAnimatedScaleProps(tickGetter(item), scale) };
      },
      default: {
        immediate: !isAnimated,
      },
      config: {
        // A higher-than-default (0.01) precision avoids jittery transitions when items on the scale
        // only have to move small distances.
        precision: 0.001,
        clamp: true,
      },
    },
    [ticks, isAnimated],
  );

  return (cb: TransitionRenderFn<TItem, TransitionProps>) => {
    return transitionFn((transitionProps, item, t, i) => {
      // Interpolate the normalized value over the current range. If the range changes but the domain
      // remains the same (e.g., due to a resize), this prevents erroneously animating to the resized
      // location, while preserving animations that reflect a change in the underlying domain.
      const interpolatedNormalizedValue = getNormalizedValueInterpolator(transitionProps.normalizedValue, range);

      // Interpolate the normalized offset, which is necessary to properly position and animate the
      // location of ticks and labels for band scales. The output range always starts at 0, since this
      // value is meant as an offset to the interpolated value (which already accounts for any offset in
      // the scale's range itself).
      const interpolatedNormalizedOffset = getNormalizedOffsetInterpolator(transitionProps.normalizedOffset, range);

      const interpolatedValue = to(
        [interpolatedNormalizedValue, interpolatedNormalizedOffset],
        (animValue, animOffset) => {
          return animOffset + animValue;
        },
      );
      return cb({ ...transitionProps, interpolatedValue }, item, t, i);
    });
  };
};
